/**
 * PillarRatingWidget — per-pillar (or overall) 1-5 quality rating + optional
 * comment. Backend stores per-admin; submitting again UPSERTs.
 *
 * Permission gate: `matches.summary.rate`.
 * Backend contract:
 *   POST /v1/admin/matches/:matchId/quality-rating
 *     body { for_user_id, locale, pillar_index, rating, comment? }
 *   GET  /v1/admin/matches/:matchId/quality-ratings
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2 / §6.10
 */
import { useEffect, useState, useCallback } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';

interface PerPillarAgg {
  pillar_index: number;
  n: number;
  avg: string | null;
}

interface Props {
  matchId: string;
  forUserId: string;
  locale: 'en' | 'es';
  /** 0..5 for the 6 pillars; -1 for the overall summary slice. */
  pillarIndex: number;
}

export function PillarRatingWidget({
  matchId,
  forUserId,
  locale,
  pillarIndex,
}: Props) {
  const apiUrl = useApiUrl();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [agg, setAgg] = useState<PerPillarAgg | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const loadAgg = useCallback(async () => {
    try {
      const r = await adminFetch(
        `${apiUrl}/v1/admin/matches/${matchId}/quality-ratings`,
      );
      const data = await r.json();
      if (pillarIndex === -1) {
        setAgg(data.aggregates?.overall ?? null);
      } else {
        const match = (data.aggregates?.per_pillar ?? []).find(
          (p: PerPillarAgg) => p.pillar_index === pillarIndex,
        );
        setAgg(match ?? null);
      }
    } catch {
      /* aggregates are decorative — swallow */
    }
  }, [apiUrl, matchId, pillarIndex]);

  useEffect(() => {
    loadAgg();
  }, [loadAgg]);

  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    setErr(null);
    setDone(false);
    try {
      await adminFetch(`${apiUrl}/v1/admin/matches/${matchId}/quality-rating`, {
        method: 'POST',
        body: JSON.stringify({
          for_user_id: forUserId,
          locale,
          pillar_index: pillarIndex,
          rating,
          comment: comment.trim() || undefined,
        }),
      });
      setComment('');
      setDone(true);
      await loadAgg();
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Rating failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded-md bg-muted p-2 text-xs">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>Rate quality:</span>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            aria-pressed={rating === n}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            className={
              rating === n
                ? 'cursor-pointer rounded border border-amber-400 bg-amber-400 px-2 py-0.5 text-xs font-bold text-black'
                : 'cursor-pointer rounded border bg-background px-2 py-0.5 text-xs text-foreground hover:bg-muted'
            }
          >
            {n}★
          </button>
        ))}
        <input
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Comment (optional)"
          aria-label="Rating comment"
          className="min-w-[120px] flex-1 rounded border bg-background px-1.5 py-1 text-xs text-foreground placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!rating || busy}
          className="cursor-pointer rounded bg-primary px-2.5 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? '…' : 'Submit'}
        </button>
        {agg && (
          <span className="text-muted-foreground">
            (avg {agg.avg ?? '—'}, n={agg.n})
          </span>
        )}
      </div>
      {err && <div className="mt-1 text-destructive">{err}</div>}
      {done && !err && (
        <div className="mt-1 text-green-500">Saved ✓</div>
      )}
    </div>
  );
}
